import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, catchError, throwError } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../auth/data-access/auth.service';

/** Shape the notification bell dropdown renders. */
export interface UiNotification {
  id: string;
  title: string;
  description: string;
  time: string;
  type: 'success' | 'warning' | 'info';
  kind: string; // raw backend type — drives the per-type icon
  read: boolean;
}

interface ApiNotification {
  _id: string;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
  metadata?: Record<string, unknown>;
}

/**
 * In-app notifications: fetch history via REST (cookie-authed, works in prod) and
 * receive live pushes over the socket (per-user room). Both feed one signal so the
 * bell shows persisted + real-time notifications identically.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly baseUrl = environment.apiBaseUrl;

  private socket?: Socket;
  private started = false;

  readonly notifications = signal<UiNotification[]>([]);
  readonly unreadCount = computed(() => this.notifications().filter((n) => !n.read).length);

  readonly backfilling = signal(false);
  private backfillFailsafe?: ReturnType<typeof setTimeout>;
  private static readonly BACKFILL_FAILSAFE_MS = 3 * 60_000;

  // Fires ONLY on a genuine backfill-completed socket event — NOT when `backfilling`
  // is cleared for teardown (logout) or the failsafe timeout. Consumers that reload
  // data / toast "calculations complete" must key off this, never off the flag going
  // false, or they'd falsely fire on a timeout or logout.
  private readonly backfillCompleted$$ = new Subject<void>();
  readonly backfillCompleted$ = this.backfillCompleted$$.asObservable();

  // A sync's Spam Shield scan hid or flagged something — live only, so pages showing counts
  // (Integrations, Transactions) can refresh and say so at the moment it happens.
  private readonly spamShield$$ = new Subject<{ connection_id?: string; spam: number; suspected: number }>();
  readonly spamShield$ = this.spamShield$$.asObservable();

  constructor() {
    // Self-managing: follow the auth state so the socket is torn down on EVERY logout
    // path — the logout button, a 401 interceptor clearing auth, or session expiry —
    // not just the button. stop() resets `started`, so a later re-login reconnects cleanly.
    effect(() => {
      if (this.auth.currentUser()) {
        this.start();
      } else {
        this.stop();
      }
    });
  }

  /** Idempotent — fetch history + open the realtime socket. Driven by the auth effect. */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.refresh();
    this.connectSocket();
  }

  /** Tear down on logout. */
  stop(): void {
    this.socket?.disconnect();
    this.socket = undefined;
    this.started = false;
    this.notifications.set([]);
    clearTimeout(this.backfillFailsafe);
    this.backfilling.set(false);
  }

  refresh(): void {
    this.http
      .get<{ data: ApiNotification[] }>(`${this.baseUrl}/notifications`, { withCredentials: true })
      .subscribe({
        next: (res) => this.notifications.set((res?.data ?? []).map((n) => this.toUi(n))),
        error: () => { /* silent — the bell just shows the empty state */ },
      });
  }

  /**
   * Optimistically mark all read, reverting to the prior state if the request fails so
   * the UI never diverges from the server. Returns the request so the caller can toast
   * success/failure. The caller MUST subscribe for the request to fire.
   */
  markAllRead(): Observable<unknown> {
    const snapshot = this.notifications();
    this.notifications.update((list) => list.map((n) => ({ ...n, read: true })));
    return this.http
      .patch(`${this.baseUrl}/notifications/read-all`, {}, { withCredentials: true })
      .pipe(
        catchError((err) => {
          this.notifications.set(snapshot); // revert — server still has them unread
          return throwError(() => err);
        }),
      );
  }

  /** Optimistically mark one read; reverts on failure. Caller must subscribe. */
  markRead(id: string): Observable<unknown> {
    const snapshot = this.notifications();
    this.notifications.update((list) => list.map((n) => (n.id === id ? { ...n, read: true } : n)));
    return this.http
      .patch(`${this.baseUrl}/notifications/${id}/read`, {}, { withCredentials: true })
      .pipe(
        catchError((err) => {
          this.notifications.set(snapshot);
          return throwError(() => err);
        }),
      );
  }

  private connectSocket(): void {
    const token = this.auth.getAccessToken(); // dev-only Bearer; prod rides the cookie
    // Start on HTTP long-polling (works through any nginx/CDN, no WS-upgrade config
    // needed) and auto-upgrade to WebSocket where the proxy supports it. Forcing
    // 'websocket' first breaks behind proxies without the Upgrade headers set.
    this.socket = io(this.baseUrl, {
      withCredentials: true,
      transports: ['polling', 'websocket'],
      ...(token ? { auth: { token } } : {}),
    });
    this.socket.on('notification', (n: ApiNotification) => {
      const ui = this.toUi(n);
      this.notifications.update((list) => [ui, ...list.filter((x) => x.id !== ui.id)].slice(0, 50));
      if (n.type === 'spam_shield') {
        const m = n.metadata ?? {};
        this.spamShield$$.next({
          connection_id: typeof m['connection_id'] === 'string' ? m['connection_id'] : undefined,
          spam: Number(m['spam']) || 0,
          suspected: Number(m['suspected']) || 0,
        });
      }
    });
    // Live price/cost-basis backfill progress (not persisted — a transient UI signal).
    this.socket.on('backfill_status', (e: { status?: string }) => {
      this.setBackfilling(e?.status === 'started');
      // Only a real 'completed' signals success — the failsafe/logout paths clear the
      // flag without this, so downstream reloads/toasts never fire on those.
      if (e?.status === 'completed') this.backfillCompleted$$.next();
    });
  }

  private setBackfilling(active: boolean): void {
    clearTimeout(this.backfillFailsafe);
    this.backfilling.set(active);
    if (active) {
      this.backfillFailsafe = setTimeout(
        () => this.backfilling.set(false),
        NotificationsService.BACKFILL_FAILSAFE_MS,
      );
    }
  }

  /** Re-arm the backfill banner after a hard refresh (the 'started' socket event fired
   *  before the reload). Only sets true — a false result must never clobber a live
   *  'started' that raced in between page load and this recovery call. */
  hydrateBackfilling(active: boolean): void {
    if (active) this.setBackfilling(true);
  }

  private toUi(n: ApiNotification): UiNotification {
    return {
      id: n._id,
      title: n.title,
      description: n.message,
      time: this.relativeTime(n.created_at),
      type: this.uiType(n.type),
      kind: n.type,
      read: !!n.is_read,
    };
  }

  private uiType(type: string): 'success' | 'warning' | 'info' {
    switch (type) {
      case 'sync_complete':
      case 'issue_completed':
        return 'success';
      case 'sync_failed':
      case 'reauth_needed':
      case 'new_device_login':
      // The only issue event that asks the user to DO something — surfacing it as a
      // warning is what stops "Need More Information" from quietly stalling forever.
      case 'issue_need_more_information':
      case 'spam_shield': // caught something — worth a look
        return 'warning';
      default:
        return 'info'; // welcome, the remaining issue events, and any future type
    }
  }

  private relativeTime(iso: string): string {
    const t = new Date(iso).getTime();
    if (!t) return '';
    const min = Math.floor((Date.now() - t) / 60000);
    if (min < 1) return 'Just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d ago`;
    return new Date(iso).toLocaleDateString();
  }
}
